import { databases, storage } from '../lib/appwrite';
import { ID, Query, Permission, Role } from 'appwrite';

// ============================================
// FOOD ITEM SERVICE
// ============================================

const DATABASE_ID = process.env.REACT_APP_APPWRITE_DATABASE_ID;
const FOODITEM_COLLECTION_ID = process.env.REACT_APP_APPWRITE_FOODITEM_COLLECTION_ID;
const BUCKET_ID = process.env.REACT_APP_APPWRITE_BUCKET_ID;

/**
 * Upload an image to Appwrite Storage
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} - The uploaded file ID
 */
export const uploadFoodImage = async (file) => {
  try {
    console.log('uploadFoodImage called with:', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      bucketId: BUCKET_ID
    });

    if (!BUCKET_ID) {
      throw new Error('BUCKET_ID is not configured in environment variables');
    }

    if (!file) {
      throw new Error('No file provided for upload');
    }

    const response = await storage.createFile(
      BUCKET_ID,
      ID.unique(),
      file
    );

    console.log('Upload response:', response);
    return response.$id;
  } catch (error) {
    console.error('Error in uploadFoodImage:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      type: error.type,
      response: error.response
    });
    throw error;
  }
};

/**
 * Delete an image file from Appwrite Storage
 * @param {string} fileId - The file ID to delete
 */
export const deleteImageFile = async (fileId) => {
  try {
    await storage.deleteFile(BUCKET_ID, fileId);
  } catch (error) {
    console.error('Error deleting image file:', error);
    throw error;
  }
};

/**
 * Get the URL for viewing an image from Appwrite Storage
 * @param {string} fileId - The file ID
 * @returns {string} - The image URL
 */
export const getFoodImageUrl = (fileId) => {
  if (!fileId) {
    console.log('No fileId provided to getFoodImageUrl');
    return '';
  }

  if (!BUCKET_ID) {
    console.error('BUCKET_ID is not configured');
    return '';
  }

  try {
    const url = storage.getFileView(BUCKET_ID, fileId);
    const urlString = url.toString();
    console.log('Generated image URL for fileId:', fileId, '→', urlString);
    return urlString;
  } catch (error) {
    console.error('Error getting image URL for fileId:', fileId, error);
    return '';
  }
};

/**
 * Create a new food item listing
 * @param {Object} foodItemData - The food item data
 * @param {string} vendorId - The vendor's user ID
 * @returns {Promise<Object>} - Created food item document
 */
export const createFoodItem = async (foodItemData, vendorId) => {
  try {
    console.log('Creating food item:', { foodItemData, vendorId });

    // Calculate hidden_after (30 days after cutoff_time) - only for listed items
    let hiddenAfter = foodItemData.hiddenAfter !== undefined ? foodItemData.hiddenAfter : null;
    if (foodItemData.listed === 'True' && foodItemData.cutoffTime) {
      try {
        const cutoff = new Date(foodItemData.cutoffTime);
        if (!isNaN(cutoff.getTime())) {
          // Set to 30 days after cutoff
          hiddenAfter = new Date(cutoff.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
        }
      } catch (e) {
        console.error('Error calculating hidden_after:', e);
      }
    }

    const foodItem = await databases.createDocument(
      DATABASE_ID,
      FOODITEM_COLLECTION_ID,
      ID.unique(),
      {
        vendor_id: vendorId,
        pickup_id: foodItemData.pickupId || null,
        item_name: foodItemData.itemName,
        price: isNaN(parseFloat(foodItemData.price)) ? 0 : parseFloat(foodItemData.price),
        description: foodItemData.description || '',
        ingredients: foodItemData.ingredients || '',
        meal_type: Array.isArray(foodItemData.mealType) ? foodItemData.mealType : [],
        dietary_label: foodItemData.dietaryLabel || 'None',
        stock_count: isNaN(parseInt(foodItemData.stockCount)) ? 0 : parseInt(foodItemData.stockCount),
        pickup_time: foodItemData.pickupTime,
        is_preorder: foodItemData.isPreorder,
        cutoff_time: foodItemData.cutoffTime,
        favourite: foodItemData.favourite || 'False',
        hidden_after: hiddenAfter,
        listed: foodItemData.listed || 'False'
      },
      [
        Permission.read(Role.any()),
        Permission.update(Role.user(vendorId)),
        Permission.delete(Role.user(vendorId))
      ]
    );

    console.log('Food item created successfully:', foodItem);
    return foodItem;
  } catch (error) {
    console.error('Error creating food item:', error);
    throw error;
  }
};

/**
 * Get all food items from the database
 * @returns {Promise<Array>} - Array of food item objects
 */
export const getAllFoodItems = async () => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      FOODITEM_COLLECTION_ID,
      [Query.equal('listed', 'True')]
    );

    // Filter out items that have a hidden_after timestamp in the past
    const now = new Date();
    const visible = response.documents.filter(doc => {
      if (!doc.hidden_after) return true;
      try {
        const hiddenAfter = new Date(doc.hidden_after);
        return hiddenAfter > now;
      } catch {
        return true;
      }
    });

    return visible;
  } catch (error) {
    console.error('Error fetching food items:', error);
    throw error;
  }
};

/**
 * Get food items by vendor ID
 * @param {string} vendorId - The vendor's user ID
 * @returns {Promise<Array>} - Array of food item objects
 */
export const getFoodItemsByVendorId = async (vendorId) => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      FOODITEM_COLLECTION_ID,
      [Query.equal('vendor_id', vendorId)]
    );

    return response.documents;
  } catch (error) {
    console.error('Error fetching vendor food items:', error);
    throw error;
  }
};

/**
 * Get favorite food items by vendor ID (limit 3)
 * @param {string} vendorId - The vendor's user ID
 * @returns {Promise<Array>} - Array of food item objects
 */
export const getFavoriteFoodItems = async (vendorId) => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      FOODITEM_COLLECTION_ID,
      [
        Query.equal('vendor_id', vendorId),
        Query.equal('favourite', 'True'),
        Query.limit(10),
        Query.orderDesc('$createdAt')
      ]
    );

    return response.documents;
  } catch (error) {
    console.error('Error fetching favorite food items:', error);
    throw error;
  }
};

/**
 * Update favorite status of a food item
 * @param {string} foodItemId - The food item ID
 * @param {string} status - 'True' or 'False'
 * @returns {Promise<Object>} - Updated food item document
 */
export const updateFavoriteStatus = async (foodItemId, status) => {
  try {
    return await updateFoodItem(foodItemId, { favourite: status });
  } catch (error) {
    console.error('Error updating favorite status:', error);
    throw error;
  }
};

/**
 * Get food items by pickup point ID
 * @param {string} pickupId - The pickup point ID
 * @returns {Promise<Array>} - Array of food item objects
 */
export const getFoodItemsByPickupId = async (pickupId) => {
  try {
    const response = await databases.listDocuments(
      DATABASE_ID,
      FOODITEM_COLLECTION_ID,
      [
        Query.equal('pickup_id', pickupId),
        Query.equal('listed', 'True')
      ]
    );

    // Filter out items that have a hidden_after timestamp in the past
    const now = new Date();
    const visible = response.documents.filter(doc => {
      if (!doc.hidden_after) return true;
      try {
        const hiddenAfter = new Date(doc.hidden_after);
        return hiddenAfter > now;
      } catch {
        return true;
      }
    });

    return visible;
  } catch (error) {
    console.error('Error fetching food items by pickup point:', error);
    throw error;
  }
};

/**
 * Get a single food item by ID
 * @param {string} foodItemId - The food item ID
 * @returns {Promise<Object>} - Food item object
 */
export const getFoodItemById = async (foodItemId) => {
  try {
    const foodItem = await databases.getDocument(
      DATABASE_ID,
      FOODITEM_COLLECTION_ID,
      foodItemId
    );

    return foodItem;
  } catch (error) {
    console.error('Error fetching food item:', error);
    throw error;
  }
};

/**
 * Get multiple food items by their IDs in a single request
 * @param {Array<string>} foodItemIds - Array of food item IDs
 * @returns {Promise<Array>} - Array of food item documents
 */
export const getFoodItemsByIds = async (foodItemIds) => {
  try {
    if (!foodItemIds || foodItemIds.length === 0) return [];
    
    const response = await databases.listDocuments(
      DATABASE_ID,
      FOODITEM_COLLECTION_ID,
      [Query.equal('$id', foodItemIds)]
    );
    
    return response.documents;
  } catch (error) {
    console.error('Error fetching batch food items:', error);
    throw error;
  }
};

/**
 * Update a food item
 * @param {string} foodItemId - The food item ID
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} - Updated food item document
 */
export const updateFoodItem = async (foodItemId, updateData) => {
  try {
    const finalUpdateData = { ...updateData };

    // If cutoff_time is being updated and item is/becoming listed, recalculate hidden_after (30 days later)
    if (updateData.cutoff_time && (updateData.listed === 'True' || finalUpdateData.listed === 'True')) {
      try {
        const cutoff = new Date(updateData.cutoff_time);
        if (!isNaN(cutoff.getTime())) {
          finalUpdateData.hidden_after = new Date(cutoff.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
        }
      } catch (e) {
        console.error('Error recalculating hidden_after:', e);
      }
    }

    const foodItem = await databases.updateDocument(
      DATABASE_ID,
      FOODITEM_COLLECTION_ID,
      foodItemId,
      finalUpdateData
    );

    return foodItem;
  } catch (error) {
    console.error('Error updating food item:', error);
    throw error;
  }
};

/**
 * Delete a food item
 * @param {string} foodItemId - The food item ID
 */
export const deleteFoodItem = async (foodItemId) => {
  try {
    await databases.deleteDocument(
      DATABASE_ID,
      FOODITEM_COLLECTION_ID,
      foodItemId
    );
  } catch (error) {
    console.error('Error deleting food item:', error);
    throw error;
  }
};

/**
 * Update stock count for a food item
 * @param {string} foodItemId - The food item ID
 * @param {number} newStockCount - The new stock count
 * @returns {Promise<Object>} - Updated food item document
 */
export const updateStockCount = async (foodItemId, newStockCount) => {
  try {
    const parsed = parseInt(newStockCount);
    const updateData = { stock_count: parsed };

    // If stock reaches zero or below, schedule hiding the item 1 hour from now
    if (parsed <= 0) {
      const hiddenAfter = new Date(Date.now() + 60 * 60 * 1000).toISOString(); // 1 hour
      updateData.hidden_after = hiddenAfter;
    } else {
      // If restocked, clear any previous hidden timestamp
      updateData.hidden_after = null;
    }

    return await updateFoodItem(foodItemId, updateData);
  } catch (error) {
    console.error('Error updating stock count:', error);
    throw error;
  }
};

// ============================================
// DATABASE SCHEMA REFERENCE
// ============================================
//
// FoodItems Collection Schema:
// Collection ID: fooditem
//
// Attributes:
// - vendor_id (String): Foreign key to the user in Profiles collection
// - pickup_id (String): Foreign key to PickupPoints collection
// - item_name (String): Name of the food item (e.g., "Chicken Breast Aglio Olio")
// - price (Float): Unit price of the meal
// - description (String): Detailed meal description
// - ingredients (String): List of ingredients
// - meal_type (Enum): Options: Breakfast, Lunch, Dinner
// - dietary_label (Enum): Options: Vegetarian, None (more can be added)
// - stock_count (Integer): Number of portions available
// - image_id (String): The ID of the uploaded photo in Appwrite Storage
// - pickup_time (Datetime): When the food will be at the pickup location
// - is_preorder (Boolean): Whether it is "Available Now" or "Pre-order Only"
// - listed (Enum): Whether the item is publicly visible (True, False)
// - hidden_after (Datetime): When the item should be automatically hidden from public view
// - cutoff_time (Datetime): Last time customers can place an order
// 
